import React, { useEffect, useState } from "react";
import { Box, ButtonBase, Menu as MuiMenu, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";

import { tokens } from "../../theme/galleryTheme";
import { LANGUAGES, getCurrentLanguage, initAutoTranslate, setLanguage } from "./autoTranslate";

const MENU_FONT = '"Bell MT", "Baskervville", "Times New Roman", serif';
const NATIVE = {
  en: "English",
  ja: "日本語",
  "zh-TW": "繁體中文",
  "zh-CN": "简体中文",
  ko: "한국어",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
};

/**
 * "EN ▾" language selector for the site header. Looks like the header's utility links
 * (small caps, secondary colour, ink on hover). The selector itself is never translated.
 */
export default function LanguageSelector({ triggerSx, compact = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [current, setCurrent] = useState(() => getCurrentLanguage());
  const open = Boolean(anchorEl);
  const currentLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  // Re-apply a previously chosen language on every page load.
  useEffect(() => {
    initAutoTranslate();
  }, []);

  const choose = (code) => {
    setAnchorEl(null);
    if (code === current) return;
    setCurrent(code);
    setLanguage(code);
  };

  return (
    <>
      <ButtonBase
        className="notranslate"
        translate="no"
        disableRipple
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        aria-controls={open ? "lg-language-menu" : undefined}
      
        sx={{
          color: tokens.color.secondary,
          fontFamily: "inherit",
          fontSize: "0.8125rem",
          fontWeight: 500,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          minHeight: compact ? 48 : 36,
          minWidth: compact ? 44 : 0,
          px: compact ? 0.5 : 0,
          "&:hover, &:focus-visible": { color: tokens.color.ink },
          "&:focus-visible": { outline: `1px solid ${tokens.color.ink}`, outlineOffset: 2 },
          ...triggerSx,
        }}
      >
        {currentLang.short}
        <KeyboardArrowDownIcon
          aria-hidden="true"
          sx={{ fontSize: 16, transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "none" }}
        />
      </ButtonBase>

      <MuiMenu
        id="lg-language-menu"
        className="notranslate"
        translate="no"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            minWidth: 190,
            borderRadius: 0,
            border: `1px solid ${tokens.color.line}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          },
        }}
        MenuListProps={{ dense: true, sx: { py: 0.75 } }}
      >
        {LANGUAGES.map((lang) => {
          const selected = lang.code === current;
          return (
            <MenuItem
              key={lang.code}
              selected={selected}
              onClick={() => choose(lang.code)}
              lang={lang.code}
              sx={{
                fontFamily: MENU_FONT,
                fontSize: "0.9375rem",
                color: selected ? tokens.color.ink : tokens.color.text,
                py: 1,
                px: 2,
                gap: 1.5,
                minHeight: 40,
                "&.Mui-selected, &.Mui-selected:hover": { bgcolor: tokens.color.ivory },
                "&:hover": { bgcolor: tokens.color.ivory },
              }}
            >
             
              {lang.code !== "en1" && lang.code !== "tl" && (
                <Box component="span" sx={{ color: tokens.color.muted, fontSize: "0.8125rem" }}>
                  {NATIVE[lang.code]}
                </Box>
              )}
              <CheckIcon
                aria-hidden="true"
                sx={{ fontSize: 16, color: tokens.color.ink, visibility: selected ? "visible" : "hidden" }}
              />
            </MenuItem>
          );
        })}
      </MuiMenu>
    </>
  );
}
